import React from 'react';
import { 
  Stack, FormControl, Select, MenuItem, TextField, Button, Typography, 
  TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Tooltip 
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Link } from 'react-router-dom';

const CorporateViewVitals = ({
  timeRange, setTimeRange, customFrom, setCustomFrom, customTo, setCustomTo,
  currentOrg, userDetails, setAlert, setNewPatient, clearFields, teamMembersData, setCaseOwner,
  searchText, handleSearchChange,
  filteredItems, page, rowsPerPage, setPage, handlePageClick,
  corporateMap
}) => {
  const getOrdinal = (n) => {
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  };

  const formatCreatedAt = (isoString) => {
    if (!isoString) return { dateLine: '—', timeLine: '—' };
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return { dateLine: '—', timeLine: '—' };

    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();

    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');

    return {
      dateLine: `${dd}/${mm}/${yyyy}`,
      timeLine: `${hh}:${min}:${ss}`,
    };
  };

  return (
    <>
      {/* Corporate Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <FormControl size="small">
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            sx={{ borderRadius: 2, minWidth: 160 }}
          >
            <MenuItem value="week">This Week</MenuItem>
            <MenuItem value="month">This Month</MenuItem>
            <MenuItem value="quarter">This Quarter</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          value={searchText}
          onChange={handleSearchChange}
          placeholder="Search User ID"
          sx={{ minWidth: 220 }}
        />

        {timeRange === 'custom' && (
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setCustomFrom('');
                setCustomTo('');
              }}
            >
              Reset
            </Button>
          </Stack>
        )}
      </Stack>

      <TableContainer component={Paper} sx={{ borderRadius: '16px', width: '100%' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left"><Typography>Created Date</Typography></TableCell>
              <TableCell align="left"><Typography>User ID</Typography></TableCell>
              <TableCell align="left"><Typography>Wellness Risk Flags</Typography></TableCell>
              <TableCell align="left"><Typography>Chat Risk Flags</Typography></TableCell>
              <TableCell align="left"><Typography>Chat Sessions</Typography></TableCell>
              <TableCell align="left"><Typography>Vitals Scans</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((patient, index) => {
                const mv = corporateMap[patient.id];
                const { dateLine, timeLine } = formatCreatedAt(patient.createdAt);
                const userId = patient?.profile?.userId || patient?.profile?.userTag || '';

                return (
                  <TableRow
                    key={index}
                    component={Link}
                    to="/vitals"
                    state={{
                      record: patient,
                      vitalsMeta: mv,
                    }}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                  >
                    <TableCell align="left">
                      <Stack spacing={0}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{dateLine}</Typography>
                        <Typography variant="caption" color="text.secondary">{timeLine}</Typography>
                      </Stack>
                    </TableCell>

                    <TableCell align="left">{userId}</TableCell>

                    <TableCell align="left">
                      {mv?.linked ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                          {mv.vitalFlags?.critical?.map((flag, i) => (
                            <Chip
                              key={`crit-${flag}-${i}`}
                              icon={<WarningAmberIcon />}
                              label={`${flag}`}
                              size="small"
                              sx={{ backgroundColor: '#FEE2E2', color: '#B91C1C', fontWeight: 600 }}
                            />
                          ))}
                          {mv.vitalFlags?.abnormal?.map((flag, i) => (
                            <Chip
                              key={`abn-${flag}-${i}`}
                              icon={<LocalHospitalIcon />}
                              label={`${flag}`}
                              size="small"
                              sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 600 }}
                            />
                          ))}
                          {mv.stressLabel && (
                            <Chip
                              icon={<MonitorHeartIcon />}
                              label="Stress"
                              size="small"
                              sx={{
                                backgroundColor: mv.stressLabel === 'High' ? '#FEE2E2' : '#FEF3C7',
                                color: mv.stressLabel === 'High' ? '#B91C1C' : '#92400E',
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {(!mv.vitalFlags?.critical?.length &&
                            !mv.vitalFlags?.abnormal?.length &&
                            !mv.stressLabel) && (
                              <Typography variant="caption" color="text.secondary">No flags</Typography>
                            )}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>

                    <TableCell align="left">
                      {mv?.linked ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                          {mv.chatRiskSignals?.HIGH?.length > 0 && (
                            <Tooltip title={`${mv.chatRiskSignals.HIGH.join(', ')}`} arrow>
                              <Chip
                                icon={<ChatBubbleOutlineIcon />}
                                label={`${mv.chatRiskSignals.HIGH.join(', ')}`}
                                size="small"
                                sx={{
                                  backgroundColor: '#FEE2E2',
                                  color: '#B91C1C',
                                  fontWeight: 600,
                                  maxWidth: 260,
                                  '& .MuiChip-label': {
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                          {mv.chatRiskSignals?.MEDIUM?.length > 0 && (
                            <Tooltip title={`${mv.chatRiskSignals.MEDIUM.join(', ')}`} arrow>
                              <Chip
                                icon={<ChatBubbleOutlineIcon />}
                                label={`${mv.chatRiskSignals.MEDIUM.join(', ')}`}
                                size="small"
                                sx={{
                                  backgroundColor: '#FEF3C7',
                                  color: '#92400E',
                                  fontWeight: 600,
                                  maxWidth: 260,
                                  '& .MuiChip-label': {
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                          {(!mv.chatRiskSignals?.HIGH?.length && !mv.chatRiskSignals?.MEDIUM?.length) && (
                            <Typography variant="caption" color="text.secondary">No chat flags</Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>

                    <TableCell align="left">
                      {mv?.linked ? mv.chatCount ?? 0 : '—'}
                    </TableCell>
                    <TableCell align="left">
                      {mv?.linked ? mv.scanCount ?? 0 : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <div style={{ 
          padding: '16px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <Typography>
            Showing data {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, filteredItems.length)} of {filteredItems.length} entries
          </Typography>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              sx={{
                minWidth: '40px',
                height: '40px',
                backgroundColor: '#f0f0f0',
                '&:hover': { backgroundColor: '#e0e0e0' }
              }}
            >
              ←
            </Button>
            {[...Array(Math.ceil(filteredItems.length / rowsPerPage))].map((_, index) => {
              const pageNum = index + 1;
              const totalPages = Math.ceil(filteredItems.length / rowsPerPage);
              const showPage = pageNum <= 3 || 
                            pageNum > totalPages - 3 || 
                            Math.abs(pageNum - (page + 1)) <= 1;

              if (!showPage && pageNum === 4) {
                return <Typography key="ellipsis1">...</Typography>;
              }
              if (!showPage && pageNum === totalPages - 3) {
                return <Typography key="ellipsis2">...</Typography>;
              }
              if (!showPage) return null;

              return (
                <Button
                  key={pageNum}
                  onClick={() => handlePageClick(pageNum)}
                  sx={{
                    minWidth: '40px',
                    height: '40px',
                    backgroundColor: page === index ? '#29C0BB' : '#f0f0f0',
                    color: page === index ? 'white' : 'inherit',
                    '&:hover': { backgroundColor: page === index ? '#29C0BB' : '#e0e0e0' }
                  }}
                >
                  {pageNum}
                </Button>
              )
            })}
            <Button
              onClick={() => setPage(Math.min(Math.ceil(filteredItems.length / rowsPerPage) - 1, page + 1))}
              disabled={page >= Math.ceil(filteredItems.length / rowsPerPage) - 1}
              sx={{
                minWidth: '40px',
                height: '40px',
                backgroundColor: '#f0f0f0',
                '&:hover': { backgroundColor: '#e0e0e0' }
              }}
            >
              →
            </Button>
          </div>
        </div>
      </TableContainer>
    </>
  );
};

export default CorporateViewVitals;